import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  TrendingUp,
  Percent,
  Globe,
  ShoppingBag,
  Share2,
  Users
} from 'lucide-react';
import { FinancialInfo } from '../../types/companyProfile';

interface FinancialInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: FinancialInfo;
  onSave: (data: FinancialInfo) => void;
}

const CURRENCIES = [
  { value: 'TRY', label: '₺ Türk Lirası', symbol: '₺' },
  { value: 'USD', label: '$ Amerikan Doları', symbol: '$' },
  { value: 'EUR', label: '€ Euro', symbol: '€' }
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Finansal analiz başlıyor...', subtext: 'Gelirinizi değerlendiriyoruz' },
  { threshold: 2, message: 'AI hesaplıyor...', subtext: 'Kar marjı önerileri hazırlanıyor' },
  { threshold: 3, message: 'Bütçe analizi...', subtext: 'Reklam ROI tahminleri oluşturuluyor' },
  { threshold: 4, message: 'Harika! Profil güçleniyor...', subtext: 'Finansal öneriler için hazırız' }
];

const STEPS = [
  { id: 'revenue', title: 'Gelir Bilgileri', icon: TrendingUp },
  { id: 'budget', title: 'Reklam Bütçesi', icon: Percent }
];

const FinancialInfoModal: React.FC<FinancialInfoModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FinancialInfo>(initialData || {
    revenueCurrency: 'TRY',
    adBudget: {}
  });
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.monthlyRevenue) count++;
    if (formData.revenueCurrency) count++;
    if (formData.targetProfitMargin) count++;
    if (formData.adBudget) {
      if (formData.adBudget.website) count++;
      if (formData.adBudget.marketplace) count++;
      if (formData.adBudget.socialMedia) count++;
      if (formData.adBudget.influencer) count++;
      if (formData.adBudget.other) count++;
    }
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const handleInputChange = (field: keyof FinancialInfo, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBudgetChange = (field: keyof NonNullable<FinancialInfo['adBudget']>, value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      adBudget: {
        ...prev.adBudget,
        [field]: value
      }
    }));
  };

  const getCurrencySymbol = () => {
    const currency = CURRENCIES.find(c => c.value === formData.revenueCurrency);
    return currency?.symbol || '₺';
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '';
    return new Intl.NumberFormat('tr-TR').format(value);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSave(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Finansal Bilgiler</h2>
                <p className="text-sm text-gray-400">Adım {currentStep + 1} / {STEPS.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    index === currentStep
                      ? 'bg-green-500/20 border border-green-500/50'
                      : index < currentStep
                        ? 'bg-green-500/10 border border-green-500/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <StepIcon className={`w-4 h-4 ${
                    index === currentStep
                      ? 'text-green-400'
                      : index < currentStep
                        ? 'text-green-400'
                        : 'text-gray-500'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    index === currentStep
                      ? 'text-green-300'
                      : index < currentStep
                        ? 'text-green-300'
                        : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-green-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 8</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {/* Step 1: Revenue Info */}
          {currentStep === 0 && (
            <div className="space-y-5">
              {/* Currency Selection */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  Para Birimi
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {CURRENCIES.map((currency) => (
                    <button
                      key={currency.value}
                      type="button"
                      onClick={() => handleInputChange('revenueCurrency', currency.value)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        formData.revenueCurrency === currency.value
                          ? 'bg-green-500/20 border-2 border-green-500 text-green-300'
                          : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      <span className="text-lg mr-2">{currency.symbol}</span>
                      {currency.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Revenue */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Aylık Ortalama Ciro
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.monthlyRevenue)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange('monthlyRevenue', value ? parseInt(value) : undefined);
                    }}
                    placeholder="100.000"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Tahmini aylık satış geliriniz</p>
              </div>

              {/* Target Profit Margin */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Percent className="w-4 h-4 text-green-400" />
                  Hedef Kar Marjı (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.targetProfitMargin || ''}
                    onChange={(e) => handleInputChange('targetProfitMargin', parseInt(e.target.value) || undefined)}
                    placeholder="15"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Satışlarınızdan beklediğiniz kar oranı</p>
              </div>
            </div>
          )}

          {/* Step 2: Ad Budget */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20 mb-4">
                <p className="text-sm text-green-300">
                  💡 Reklam bütçenizi kanal bazında ayırmanız, AI&apos;ın ROAS (Reklam Getirisi) hesaplamalarını daha doğru yapmasını sağlar.
                </p>
              </div>

              {/* Website Ads */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Globe className="w-4 h-4 text-blue-400" />
                  Web Sitesi Reklamları
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.adBudget?.website)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleBudgetChange('website', value ? parseInt(value) : undefined);
                    }}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Google Ads, Bing Ads vb.</p>
              </div>

              {/* Marketplace Ads */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <ShoppingBag className="w-4 h-4 text-orange-400" />
                  Pazaryeri Reklamları
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.adBudget?.marketplace)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleBudgetChange('marketplace', value ? parseInt(value) : undefined);
                    }}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Trendyol, Hepsiburada, n11 reklamları</p>
              </div>

              {/* Social Media Ads */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Share2 className="w-4 h-4 text-pink-400" />
                  Sosyal Medya Reklamları
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-pink-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.adBudget?.socialMedia)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleBudgetChange('socialMedia', value ? parseInt(value) : undefined);
                    }}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Meta, TikTok, Twitter Ads vb.</p>
              </div>

              {/* Influencer Marketing */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  Influencer Marketing
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.adBudget?.influencer)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleBudgetChange('influencer', value ? parseInt(value) : undefined);
                    }}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Sosyal medya fenomenleri ile işbirlikleri</p>
              </div>

              {/* Other */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  Diğer Reklam Harcamaları
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="text"
                    value={formatCurrency(formData.adBudget?.other)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleBudgetChange('other', value ? parseInt(value) : undefined);
                    }}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-gray-500/50 focus:ring-1 focus:ring-gray-500/50 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Sponsorluklar, etkinlikler vb.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="w-4 h-4" />
                  Kaydet
                </>
              ) : (
                <>
                  İleri
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FinancialInfoModal;
